import { ApolloError } from "@apollo/client";
import getLocalString from "constants/Localization";
import { useCustomDeleteProposalMutation } from "generated/graphql";
import useGlobalMessage from "hooks/notifications/useGlobalMessage";
import { useCallback, useMemo } from "react";
import { useBitumioContext } from "providers/BitumioContext";
import getGraphQLErrorMessage from "utils/graphQL/getGraphQLErrorMessage";

const isAlreadyDeletedMessage = (message: string) => {
  const normalized = message.toLowerCase();
  return (
    normalized.includes("not found") ||
    normalized.includes("already deleted") ||
    normalized.includes("no query results")
  );
};

const isAlreadyDeletedError = (error: unknown) => {
  const apolloError = error as ApolloError | undefined;
  const messages = [
    apolloError?.message,
    ...(apolloError?.graphQLErrors?.map((graphQLError) => graphQLError.message) ?? []),
  ].filter(Boolean) as string[];

  return messages.some(isAlreadyDeletedMessage);
};

export default function useDeleteProposal() {
  const [{ showLoader, hideLoader }] = useBitumioContext();
  const { show } = useGlobalMessage();
  const [mutation, { loading, data, error }] = useCustomDeleteProposalMutation({
    fetchPolicy: "no-cache",
    refetchQueries: ["getProposals", "customGetProposals"],
    awaitRefetchQueries: true,
  });

  const isSuccess = useCallback((graphqlData) => !!graphqlData?.deleteProposal, []);
  const getData = useCallback(({ data: graphqlData }) => graphqlData?.deleteProposal ?? null, []);

  const deleteProposal = useCallback(
    async (options) => {
      showLoader();
      try {
        const result = await mutation(options);
        if (isSuccess(result.data)) {
          show({ message: getLocalString("proposals.delete-success") });
        } else {
          show({
            message: getLocalString("proposals.delete-failure"),
            options: { variant: "error" },
          });
        }
        return result;
      } catch (mutationError) {
        if (isAlreadyDeletedError(mutationError)) {
          return { alreadyDeleted: true, error: mutationError };
        }

        show({
          message: getGraphQLErrorMessage(
            mutationError,
            getLocalString("proposals.delete-failure")
          ),
          options: { variant: "error" },
        });
        return mutationError;
      } finally {
        hideLoader();
      }
    },
    [hideLoader, isSuccess, mutation, show, showLoader]
  );

  const context = useMemo(
    () => ({
      data: getData({ data }),
      loading,
      error,
      getData,
      isSuccess,
      isAlreadyDeletedError,
    }),
    [data, error, getData, isSuccess, loading]
  );

  return [deleteProposal, context] as const;
}
